/**
 * FHIR search helpers for the referral management UI (no bot).
 */

import type { MedplumClient } from '@medplum/core';
import type { Task } from '@medplum/fhirtypes';
import { REFERRAL_REQUEST_TASK_CODE, TASK_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import {
  isReferralRequestTask,
  referralTaskStatusToUiLabel,
} from '../fhir-model/resources/referral-task';
import type { ResourceScope } from './rbac/scopeAccess';

export type ReferralUiStatusLabel = ReturnType<typeof referralTaskStatusToUiLabel>;

const TASK_CODE_SEARCH = `${TASK_TYPE_SYSTEM}|${REFERRAL_REQUEST_TASK_CODE}`;

export interface SearchReferralRequestTasksOptions {
  scope: ResourceScope;
  /** Required when scope is PRACTICE (validates caller has a practice org). */
  practiceOrganizationRef?: string;
  /** UI status buckets; passed to FHIR `status` search and refined with `referralTaskStatusToUiLabel`. */
  statuses?: ReferralUiStatusLabel[];
}

/** Map UI buckets to FHIR Task.status tokens for `Task?status=…` (superset when buckets overlap). */
export function fhirStatusSearchParam(uiStatuses: ReferralUiStatusLabel[]): string | undefined {
  if (uiStatuses.length === 0) return undefined;

  const codes = new Set<string>();
  for (const ui of uiStatuses) {
    switch (ui) {
      case 'Cancelled':
        codes.add('cancelled');
        break;
      case 'Completed':
        codes.add('completed');
        break;
      case 'In Progress':
        codes.add('in-progress');
        codes.add('requested');
        break;
      case 'Pending':
        codes.add('requested');
        break;
      default:
        break;
    }
  }

  return [...codes].sort().join(',');
}

export function filterReferralTasksByUiStatus(
  tasks: Task[],
  statuses: ReferralUiStatusLabel[] | undefined,
): Task[] {
  if (statuses == null) return tasks;
  if (statuses.length === 0) return [];
  const allowed = new Set(statuses);
  return tasks.filter((t) => allowed.has(referralTaskStatusToUiLabel(t)));
}

export async function searchReferralRequestTasks(
  medplum: MedplumClient,
  options: SearchReferralRequestTasksOptions,
): Promise<Task[]> {
  const { scope, practiceOrganizationRef, statuses } = options;

  if (scope === 'PRACTICE' && !practiceOrganizationRef?.startsWith('Organization/')) {
    throw new Error('No practice organization is linked to your profile.');
  }

  if (statuses && statuses.length === 0) {
    return [];
  }

  const params: Record<string, string> = {
    code: TASK_CODE_SEARCH,
    _sort: '-_lastUpdated',
    _count: '200',
  };

  const fhirStatus = fhirStatusSearchParam(statuses ?? []);
  if (fhirStatus) {
    params.status = fhirStatus;
  }

  const bundle = await medplum.searchResources('Task', params);

  let tasks = (bundle as Task[]).filter(isReferralRequestTask);
  if (statuses && statuses.length > 0) {
    tasks = filterReferralTasksByUiStatus(tasks, statuses);
  }

  return tasks;
}
